import { Navigate, useLocation } from 'react-router-dom';
import { gitlabAuthorizeUrl } from '../api/endpoints';
import { useAuth } from '../auth/AuthProvider';
import { ThemeToggle } from '../components/layout/ThemeToggle';
import { ErrorNotice } from '../components/ui/Notice';
import { Spinner } from '../components/ui/Skeleton';
import { LocalLoginForm } from './LocalLoginForm';

/**
 * TWO PATHS, VISIBLY SEPARATED. GitLab OAuth for the team; email + password + a mandatory
 * authenticator code for the people who are not in GitLab (the PO case db/003 was written for).
 *
 * ⭐⭐ THE SEPARATOR IS DOING REAL WORK, not decoration. These are not two ways to reach one account
 * — a person exists on EXACTLY ONE path, enforced by `app_user.email` being unique across both. If
 * they were presented as interchangeable options, someone whose GitLab sign-in failed would try the
 * password form, get "invalid email or password" for an account that genuinely exists, and conclude
 * their account is broken. The rule ("your GitLab identity, or an account an admin created") is
 * therefore stated on the screen rather than left to be inferred from a failure.
 *
 * 🔴 The important behaviour is still the negative one: when the session check FAILED rather than
 * returned "not signed in", this page does not pretend to be a normal login screen. Offering
 * "Sign in with GitLab" while the API is unreachable sends someone into an OAuth round-trip that
 * cannot succeed and makes them think their account is broken.
 */
export function LoginPage() {
  const { state, recheck } = useAuth();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from ?? '/backlog';

  if (state.status === 'authenticated') return <Navigate to={returnTo} replace />;

  return (
    <main className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <span aria-hidden="true" className="inline-block size-6 rounded bg-[color:var(--color-accent)]" />
              PlanHub
            </h1>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              Planning for the Trade Beam team.
            </p>
          </div>
          <ThemeToggle />
        </div>

        {state.status === 'checking' ? (
          <p className="flex items-center gap-2 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
            <Spinner label="Checking session" /> Checking your session…
          </p>
        ) : null}

        {state.status === 'unknown' ? (
          <ErrorNotice error={state.error} onRetry={recheck} />
        ) : null}

        <a
          href={gitlabAuthorizeUrl()}
          aria-disabled={state.status !== 'anonymous'}
          className={`flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[color:var(--color-accent)] text-sm font-semibold text-[color:var(--color-on-accent)] ${
            state.status === 'anonymous' ? 'hover:brightness-110' : 'pointer-events-none opacity-50'
          }`}
        >
          <GitLabMark />
          Sign in with GitLab
        </a>

        <p className="text-xs text-[color:var(--color-ink-subtle)]">
          Your PlanHub identity is your GitLab identity, so the person a ticket is assigned to is the
          same person who authored the merge request.
        </p>

        {/* ⭐ A labelled divider, not a bare rule. "or" alone would read as two ways into the same
            account; naming the second path says these are different accounts for different people. */}
        <div className="flex items-center gap-3" role="separator" aria-label="or use a local account">
          <span className="h-px flex-1 bg-[color:var(--color-line)]" />
          <span className="text-xs uppercase tracking-wide text-[color:var(--color-ink-subtle)]">
            or, for accounts without GitLab
          </span>
          <span className="h-px flex-1 bg-[color:var(--color-line)]" />
        </div>

        <LocalLoginForm />
      </div>
    </main>
  );
}

/** Our own mark, drawn here — no remote asset, no user-supplied URL. */
function GitLabMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 15 5.1 6.1h5.8L8 15z" fill="#e24329" />
      <path d="M8 15 1.2 6.1h3.9L8 15z" fill="#fc6d26" />
      <path d="M1.2 6.1 .3 9a.6.6 0 0 0 .2.7L8 15 1.2 6.1z" fill="#fca326" />
      <path d="M1.2 6.1h3.9L3.4 1a.3.3 0 0 0-.6 0L1.2 6.1z" fill="#e24329" />
      <path d="M8 15l2.9-8.9h3.9L8 15z" fill="#fc6d26" />
      <path d="M14.8 6.1l.9 2.9a.6.6 0 0 1-.2.7L8 15l6.8-8.9z" fill="#fca326" />
      <path d="M14.8 6.1h-3.9L12.6 1a.3.3 0 0 1 .6 0l1.6 5.1z" fill="#e24329" />
    </svg>
  );
}
